import { useCart } from "@/contexts/CartContext";
import { X, Plus, Minus, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { trackInitiateCheckout } from "@/lib/meta";
import { getUtmQueryString } from "@/lib/attribution";
import { ga4BeginCheckout } from "@/lib/ga4";
import { formatCartName, shopifyImg } from "@/lib/format";
import { gpcEnabled } from "@/lib/privacy";
import TrustLine from "@/components/TrustLine";
import { TRUST_LINE_CART } from "@/lib/site";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQuantity, removeItem, total, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const SHOPIFY_DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN;

  // Build a Shopify cart permalink from the cart. Checkout uses this directly so it
  // never depends on the backend (no cold-start / load risk on the critical path).
  const buildPermalink = () => {
    const parts = items
      .map((i) => {
        const num = (i.variant_id || "").toString().split("/").pop();
        return /^\d+$/.test(num) ? `${num}:${i.quantity}` : null;
      })
      .filter(Boolean);
    if (!parts.length || !SHOPIFY_DOMAIN) return null;
    const qs = [];
    const utmQs = getUtmQueryString();
    if (utmQs) qs.push(utmQs);
    if (gpcEnabled()) qs.push("attributes[gpc]=1");
    return `https://${SHOPIFY_DOMAIN}/cart/${parts.join(",")}${qs.length ? `?${qs.join("&")}` : ""}`;
  };

  const handleCheckout = () => {
    setLoading(true);
    setErrorMsg("");
    trackInitiateCheckout(items, total);
    ga4BeginCheckout(items, total);

    // Go straight to Shopify via the cart permalink — no backend call.
    const permalink = buildPermalink();
    if (permalink) {
      clearCart();
      window.location.href = permalink;
      return;
    }

    setErrorMsg("We couldn't start checkout. Please tap Checkout again.");
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div data-testid="cart-overlay" className="fixed inset-0 bg-black/70 z-[60]" onClick={() => setIsOpen(false)} />
      <div data-testid="cart-drawer" className="fixed top-0 right-0 h-full w-full max-w-md bg-[#0A0A0A] z-[70] flex flex-col border-l border-[#8A8A8A]/20">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-6 border-b border-[#8A8A8A]/20">
          <h2 className="font-['Oswald'] text-lg font-medium uppercase tracking-[0.2em] text-[#F2F0EB]">Cart ({items.length})</h2>
          <button data-testid="cart-close" onClick={() => setIsOpen(false)} className="p-1 text-[#8A8A8A] hover:text-[#F2F0EB] transition-colors"><X size={20} /></button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-[#8A8A8A]/50">
              <ShoppingBag size={44} strokeWidth={1} />
              <p className="mt-4 text-xs uppercase tracking-[0.2em]">Your cart is empty</p>
            </div>
          ) : (
            <div className="space-y-8">
              {items.map((item) => (
                <div key={item.variant_id} data-testid={`cart-item-${item.product_id}`} className="flex gap-4">
                  <Link to={`/products/${item.product_id}`} onClick={() => setIsOpen(false)} className="w-16 h-20 bg-[#141414] flex-shrink-0 overflow-hidden block hover:opacity-80 transition-opacity">
                    {item.image ? <img src={shopifyImg(item.image, 160)} alt={item.name} width="64" height="80" loading="lazy" className="w-full h-full object-cover" /> : <ShoppingBag size={16} className="text-[#8A8A8A]/40 m-auto" />}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/products/${item.product_id}`} onClick={() => setIsOpen(false)}>
                      <h4 className="font-['Oswald'] text-[11px] uppercase tracking-[0.12em] text-[#F2F0EB] truncate">{formatCartName(item)}</h4>
                    </Link>
                    <p className="text-[10px] text-[#8A8A8A] mt-1">{item.size}{item.color ? ` / ${item.color}` : ""}</p>
                    <p className="text-sm text-[#F2F0EB] mt-1">${item.price.toFixed(2)}</p>
                    <div className="flex items-center gap-3 mt-2.5">
                      <button data-testid={`cart-decrease-${item.product_id}`} onClick={() => updateQuantity(item.variant_id, item.quantity - 1)} className="w-6 h-6 border border-[#8A8A8A]/30 flex items-center justify-center text-[#8A8A8A] hover:text-[#F2F0EB] hover:border-[#F2F0EB]/40 transition-colors"><Minus size={10} /></button>
                      <span className="text-xs text-[#F2F0EB] w-4 text-center">{item.quantity}</span>
                      <button data-testid={`cart-increase-${item.product_id}`} onClick={() => updateQuantity(item.variant_id, item.quantity + 1)} className="w-6 h-6 border border-[#8A8A8A]/30 flex items-center justify-center text-[#8A8A8A] hover:text-[#F2F0EB] hover:border-[#F2F0EB]/40 transition-colors"><Plus size={10} /></button>
                      <button data-testid={`cart-remove-${item.product_id}`} onClick={() => removeItem(item.variant_id)} className="ml-auto text-[10px] text-[#8A8A8A] hover:text-[#F2F0EB] uppercase tracking-[0.15em] transition-colors">Remove</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-[#8A8A8A]/20 px-6 py-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs uppercase tracking-[0.2em] text-[#8A8A8A]">Total</span>
              <span data-testid="cart-total" className="text-lg text-[#F2F0EB]">${total.toFixed(2)}</span>
            </div>
            <TrustLine text={TRUST_LINE_CART} className="mb-4" onLinkClick={() => setIsOpen(false)} />
            {errorMsg && (
              <p data-testid="cart-checkout-error" className="text-[11px] text-[#F2F0EB] bg-[#2a1a1a] border border-[#F2F0EB]/20 px-3 py-2 mb-3 tracking-[0.05em]">{errorMsg}</p>
            )}
            <button data-testid="cart-checkout-button" onClick={handleCheckout} disabled={loading} className="w-full bg-[#F2F0EB] text-[#0A0A0A] py-4 font-['Oswald'] text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#8A8A8A] transition-colors disabled:opacity-50">
              {loading ? "Processing..." : "Checkout"}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
